import { GraphemePanelsMnemonicsTabContext } from "#grapheme-panels-mnemonics-tab-context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useCallback } from "react";

/**
 * Returns a factory that builds the `onPress` handler for the mnemonics tab at
 * `tabIndex`. The handler navigates the mnemonics pager to that tab (no-op when
 * it is already active or the index is out of range), mirroring the
 * navigate-based `setActiveIndex` the tab bar used to thread down as a prop.
 *
 * The handler only navigates; it never plays audio. The panel's focused tab renders a
 * `PinyinSoundButton`, and a Learn card's binder reading section plays its own reading
 * alongside this handler when pressed. In the learn layout the
 * lesson card reveal and "Next Pronunciation" button call `playSequence` /
 * `playForPinyin` from `NewMorphemeLessonLayoutContext` directly inside their press
 * handlers — audio is never triggered by a mount or navigation effect.
 */
export function useGraphemePanelsMnemonicsTabPress() {
  const [activeIndex, navigate, routeNames] = useContextSelectors(
    GraphemePanelsMnemonicsTabContext,
    (c) => c.activeIndex,
    (c) => c.navigate,
    (c) => c.routeNames,
  );

  return useCallback(
    (tabIndex: number) => () => {
      const routeName = routeNames[tabIndex];
      if (routeName !== undefined && tabIndex !== activeIndex) {
        navigate(routeName);
      }
    },
    [activeIndex, navigate, routeNames],
  );
}
